import { Tab } from '@headlessui/react';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { LoaderFunctionArgs, json } from '@remix-run/cloudflare';
import { Link, Outlet, useFetcher, useLoaderData } from '@remix-run/react';
import BigNumber from 'bignumber.js';
import { useEffect, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import { DataBlock } from "~/components/data-block";
import TransactionButton from '~/components/transaction-button';
import { trackEvent } from '~/lib/analytics';
import { setDbFromContext } from '~/lib/db';
import { generateAddStakeManifest, generateUnstakingManifest } from '~/lib/manifests';
import { getPairByBaseToken } from '~/lib/models/pair';
import { getTokenStakingByToken } from '~/lib/models/staking';
import { SelectToken } from '~/lib/models/tokens';
import { ROUND_UP, classNames, getIconUrl, onIconError, prettyValue } from '~/lib/util';
import { fetchTokensDetails, useGatewayApi } from '~/rdt/gateway';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';

export const sASTRL_RESOURCE_ADDRESS = 'resource_rdx1tkm0z9pkz0e59yf2258drzjwaq7axtcf2mupjz2ag9kwww4jvdfd6c';
export const STAKING_POOL_ADDRESS = 'pool_rdx1c325zs6dz3un8ykkjavy9fkvvyzarkaehgsl408qup6f95aup3le3w';


export async function loader({ params, context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   const { state } = useGatewayApi;

   const [staking] = await getTokenStakingByToken(params.address);

   if (!staking) {
      return json({
         error: 'Token does not support staking',
         token: null,
         sToken: null,
         ratio: null,
         totalStake: null,
      });
   }
   
   const tokens = await fetchTokensDetails([params.address, staking.sToken]);

   const astrl = tokens.find((t) => t.address === params.address);
   const sAstrl = tokens.find((t) => t.address === staking.sToken);

   // fetch balance
   const poolDetails = await state.getEntityDetailsVaultAggregated(staking.pool);
   const totalStake = poolDetails.fungible_resources.items[0].vaults.items[0].amount;

   const ratio = new BigNumber(totalStake).div(sAstrl.totalSupply).toFixed();

   // calculate APY


   return json({
      error: null,
      token: astrl,
      sToken: sAstrl,
      ratio,
      totalStake,
      staking
   });
}

export default function TokenStaking() {
   const { token, sToken, ratio, totalStake, staking } = useLoaderData<typeof loader>();
   const walletFetcher = useFetcher<typeof walletLoader>();
   const { sendTransaction } = useRadix();

   const [account, setAccount] = useState<Account | null>(null);
   const [inputToken, setInputToken] = useState<SelectToken>(token);
   const [outputToken, setOutputToken] = useState<SelectToken>(sToken);
   const [inputAmount, setInputAmount] = useState<string>('');
   const [outputAmount, setOutputAmount] = useState<string>('');
   const [balance, setBalance] = useState<string>('');
   const [activeTab, setActiveTab] = useState<number>(0);

     function onAccount(account: Account) {
        setAccount(account.address);

        walletFetcher.load(`/wallet/${account.address}`);
     }
   
      function setRelativeInputAmount(input: string) {
         setInputAmount(new BigNumber(balance).times(input).toFixed(inputToken.divisibility, ROUND_UP));
      }
   
   useEffect(() => {
      if (!walletFetcher.data) {
         return;
      }

      setBalance(walletFetcher.data?.filter((b) => b.address == inputToken.address)[0]?.amount || '0');
   }, [walletFetcher.data, inputToken]);

   useEffect(() => {
      if (inputAmount == '') {
         return;
      }

      let output = new BigNumber(inputAmount).div(ratio).toFixed();

      if (activeTab == 1) {
         output = new BigNumber(inputAmount).times(ratio).toFixed();
      }

      setOutputAmount(output);
   }, [inputAmount]);

   useEffect(() => {
      console.log(activeTab)

      if (activeTab == 0) {
         setInputToken(token);
         setOutputToken(sToken);
      } else {
         setInputToken(sToken);
         setOutputToken(token);
      }      

      setInputAmount('');
      setOutputAmount('');
   }, [activeTab]);

   async function onAddStake() {
      if (!account) {
         return;
      }

      const transactionManifest = generateAddStakeManifest(account, token, inputAmount, staking.address);

      await sendTransaction(transactionManifest, `Stake ${token.symbol} via DefiPlaza`);

     trackEvent("send_tx", {
       type: "addStake",
     });

      setInputAmount('');
      walletFetcher.load(`/wallet/${account}`);
   }

   async function onRemoveStake() {
      if (!account) {
         return;
      }

      const transactionManifest = generateUnstakingManifest(account, sToken, inputAmount, staking.address);

      await sendTransaction(transactionManifest, `Unstake ${token.symbol} via DefiPlaza`);

     trackEvent("send_tx", {
       type: "removeStake",
     });

      setInputAmount('');
      walletFetcher.load(`/wallet/${account}`);
   }

   return (
     <div className="space-y-8">
       <dl className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-4">
         <DataBlock title="Staking APY" data={`${(Math.round(((+staking.intervalAmount * (52 / +staking.interval)) / totalStake) * 1000) / 10).toLocaleString()}%`} />
         <DataBlock title="Rewards" data={`${(+staking.intervalAmount).toLocaleString()} / ${staking.interval !== "1" ? `${staking.interval} weeks` : "week"}`} />
         <DataBlock title={`s${token.symbol} Price`} data={`${prettyValue(ratio)} ${token.symbol}`} />
         <DataBlock title={`Total ${token.symbol} Staked`} data={`${prettyValue(totalStake)} ${token.symbol}`} />
       </dl>

       <AccountSelector onAccount={onAccount}></AccountSelector>

       <Tab.Group
         onChange={(index) => {
           setActiveTab(index);
         }}
       >
         <Tab.List className="my-8 flex space-x-1 rounded-xl bg-white p-1 text-black">
           <Tab
             key="add"
             className={({ selected }) =>
               classNames(selected ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-l-md bg-white px-3 py-4 text-xl ring-inset hover:bg-gray-50 focus:z-10")
             }
           >
             Add Stake
           </Tab>
           <Tab
             key="remove"
             className={({ selected }) =>
               classNames(selected ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-r-md bg-white px-3 py-4 text-xl ring-inset hover:bg-gray-50 focus:z-10")
             }
           >
             Remove Stake
           </Tab>
         </Tab.List>
         <Tab.Panels>
           <Tab.Panel key="add">
             <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
               <div className="flex items-center justify-between">
                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                   You stake
                 </label>
                 {walletFetcher.data ? (
                   <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-right">Balance: {(Math.round(+balance * 1000000) / 1000000).toLocaleString()}</span>
                 ) : null}
               </div>
               <div className="flex items-center justify-between space-x-4">
                 <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                   <span className="flex items-center">
                     <img src={getIconUrl(token.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                     <span className="ml-3 block text-xl font-medium">{token.symbol}</span>
                     {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                   </span>
                 </div>
                 <input
                   type="text"
                   value={inputAmount ? parseFloat(inputAmount) : ""}
                   onChange={(evt) => setInputAmount(evt.target.value)}
                   placeholder="0.00"
                   autoComplete="off"
                   spellCheck="false"
                   className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                 />
               </div>
               {walletFetcher.data && +balance > 0 ? (
                 <>
                   <p className="text-primary-500 mt-6 text-right text-sm font-normal">{Math.round((+inputAmount / +balance) * 100)}%</p>
                   <input
                     id="steps-range"
                     type="range"
                     min="0"
                     max="1"
                     value={new BigNumber(inputAmount || 0).div(balance).toFixed(token.divisibility, ROUND_UP) || "0"}
                     onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setRelativeInputAmount((evt.target as HTMLInputElement).value)}
                     step="0.01"
                     className="bg-primary-100 ring-primary-300 mb-3 mt-2 h-2 w-full cursor-pointer appearance-none rounded-lg"
                   />

                   <div className="flex w-full items-center justify-between space-x-1">
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       0%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.25")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       25%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.5")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       50%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.75")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       75%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("1")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       100%
                     </button>
                   </div>
                 </>
               ) : null}
             </section>

             <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
               <div className="flex items-center justify-between">
                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                   You receive
                 </label>
               </div>
               <div className="flex items-center justify-between space-x-4">
                 <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                   <span className="flex items-center">
                     <img src={getIconUrl(sToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                     <span className="ml-3 block text-xl font-medium">{sToken.symbol}</span>
                     {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                   </span>
                 </div>
                 <input
                   type="text"
                   value={outputAmount ? parseFloat(outputAmount) : ""}
                   readOnly={true}
                   placeholder="0.00"
                   autoComplete="off"
                   spellCheck="false"
                   className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                 />
               </div>
             </section>

             <TransactionButton title={`Stake ${token.symbol}`} onClick={onAddStake} />
           </Tab.Panel>

           <Tab.Panel key="remove">
             <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
               <div className="flex items-center justify-between">
                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                   You unstake
                 </label>
                 {walletFetcher.data ? (
                   <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-right">Balance: {(Math.round(+balance * 1000000) / 1000000).toLocaleString()}</span>
                 ) : null}
               </div>
               <div className="flex items-center justify-between space-x-4">
                 <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                   <span className="flex items-center">
                     <img src={getIconUrl(sToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                     <span className="ml-3 block text-xl font-medium">{sToken.symbol}</span>
                     {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                   </span>
                 </div>
                 <input
                   type="text"
                   value={inputAmount ? parseFloat(inputAmount) : ""}
                   onChange={(evt) => setInputAmount(evt.target.value)}
                   placeholder="0.00"
                   autoComplete="off"
                   spellCheck="false"
                   className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                 />
               </div>
               {walletFetcher.data && +balance > 0 ? (
                 <>
                   <p className="text-primary-500 mt-6 text-right text-sm font-normal">{Math.round((+inputAmount / +balance) * 100)}%</p>
                   <input
                     id="steps-range"
                     type="range"
                     min="0"
                     max="1"
                     value={new BigNumber(inputAmount || 0).div(balance).toFixed(token.divisibility, ROUND_UP) || "0"}
                     onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setRelativeInputAmount((evt.target as HTMLInputElement).value)}
                     step="0.01"
                     className="bg-primary-100 ring-primary-300 mb-3 mt-2 h-2 w-full cursor-pointer appearance-none rounded-lg"
                   />

                   <div className="flex w-full items-center justify-between space-x-1">
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       0%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.25")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       25%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.5")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       50%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("0.75")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       75%
                     </button>
                     <button
                       type="button"
                       onClick={() => setRelativeInputAmount("1")}
                       className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                     >
                       100%
                     </button>
                   </div>
                 </>
               ) : null}
             </section>

             <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
               <div className="flex items-center justify-between">
                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                   You receive
                 </label>
               </div>
               <div className="flex items-center justify-between space-x-4">
                 <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                   <span className="flex items-center">
                     <img src={getIconUrl(token.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                     <span className="ml-3 block text-xl font-medium">{token.symbol}</span>
                     {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                   </span>
                 </div>
                 <input
                   type="text"
                   value={outputAmount ? parseFloat(outputAmount) : ""}
                   readOnly={true}
                   placeholder="0.00"
                   autoComplete="off"
                   spellCheck="false"
                   className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                 />
               </div>
             </section>

             <TransactionButton title={`Unstake ${sToken.symbol}`} onClick={onRemoveStake} />
           </Tab.Panel>
         </Tab.Panels>
       </Tab.Group>
     </div>
   );
}
